import { useEffect, useId, useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { ChevronRight, GripVertical } from 'lucide-react'

import type { AnyDataGridContract } from '../lib/types'
import { useLoadMore } from '@/lib/use-load-more'
import { DRAGGED_ITEM, useSortable } from '@/lib/use-sortable'
import { cn } from '@/lib/utils'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { Button } from '@/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/ui/dropdown-menu'
import { DataTable, SortableTh, Th } from '@/ui/data-table'
import { FilterMultiSelect } from '@/ui/filter-multi-select'
import { LoadMoreSentinel } from '@/ui/load-more-sentinel'
import { LoadingNotice } from '@/ui/loading-notice'
import { ResizeHandle } from '@/ui/resize-handle'
import { RetryNotice } from '@/ui/retry-notice'
import { SearchToolbar } from '@/ui/search-toolbar'
import { VirtualTableRows } from '@/ui/virtual-table-rows'

import {
  DataGridContext,
  type DataGridProps,
  useGridContext,
} from './data-grid-context'
import {
  DataGridActiveFilters,
  DataGridFilter,
  DataGridFilterControl,
  DataGridFilters,
} from './data-grid-filters'
import { DataGridSavedViewTabs } from './data-grid-saved-view-tabs'
import { DataGridSavedViews as DataGridSavedViewsControl } from './data-grid-saved-views'
import { DataGridExportMenu, DataGridToolbarMenu } from './data-grid-toolbar-menu'

const MIN_COLUMN_WIDTH = 80
const MAX_COLUMN_WIDTH = 1_200

type ColumnSizing = {
  layoutKey: string
  widths: Record<string, number>
  manual: ReadonlySet<string>
}

const AUTO_COLUMN_SIZING: ColumnSizing = {
  layoutKey: '',
  widths: {},
  manual: new Set(),
}

const DRAGGED_CELL = 'border-primary/60 border-x border-dashed bg-primary/5 opacity-60'

type ColumnReorder = {
  hintId: string
  position: number
  count: number
  dragged: boolean
  onMoveKey: (step: -1 | 1) => boolean
  onDragStart: (event: DragEvent<HTMLElement>) => void
  onDragOver: (event: DragEvent) => void
  onDragEnd: () => void
}

function DataGridRoot<Contract extends AnyDataGridContract>(props: DataGridProps<Contract>) {
  return (
    <DataGridContext value={props as unknown as DataGridProps<AnyDataGridContract>}>
      <div data-slot="data-grid" className="flex min-h-0 flex-1 flex-col">
        {props.children ?? (
          <>
            {props.savedViewTabs ? <DataGridSavedViewTabs /> : null}
            <DataGridToolbar />
            <DataGridTable />
          </>
        )}
        <DataGridPageControls />
      </div>
    </DataGridContext>
  )
}

function DataGridToolbar() {
  const { grid, assistantFilter, searchLabel = grid.contract.id, toolbarActions } = useGridContext()
  return (
    <SearchToolbar
      value={grid.searchInput}
      onChange={grid.setSearch}
      label={`Search ${searchLabel}`}
      placeholder={`Search ${searchLabel}…`}
      assistantFilter={assistantFilter ?? grid.contract.id}
      maxLength={200}
    >
      {grid.searchInput.trim().length > 0
        && grid.searchInput.trim().length < grid.contract.searchMinimumLength ? (
          <span className="text-muted-foreground text-[11px]">
            Type at least {grid.contract.searchMinimumLength} characters to search.
          </span>
        ) : null}
      {toolbarActions}
      <DataGridActiveFilters />
      {grid.canExport ? <DataGridExportMenu /> : null}
      <DataGridToolbarMenu />
    </SearchToolbar>
  )
}

function DataGridSearch() {
  const { grid, assistantFilter, searchLabel = grid.contract.id } = useGridContext()
  return (
    <SearchToolbar
      value={grid.searchInput}
      onChange={grid.setSearch}
      label={`Search ${searchLabel}`}
      placeholder={`Search ${searchLabel}…`}
      assistantFilter={assistantFilter ?? grid.contract.id}
      maxLength={200}
    />
  )
}

function DataGridLoadMore() {
  const { grid, noun = 'items' } = useGridContext()
  if (!grid.canLoadMore) return null
  return (
    <Button
      type="button"
      variant="outline"
      disabled={grid.query.isFetchingNextPage}
      onClick={() => void grid.query.fetchNextPage()}
    >
      {grid.query.isFetchingNextPage ? 'Loading…' : `Load more ${noun}`}
    </Button>
  )
}

function DataGridSavedViews() {
  const { grid } = useGridContext()
  return <DataGridSavedViewsControl savedViews={grid.savedViews} />
}

function DataGridPageControls() {
  const { grid, noun = 'items' } = useGridContext()
  const pagination = grid.pagination
  if (!pagination || !grid.hasDisplayedData) return null
  const range = pagination.start === 0 ? '0' : `${pagination.start.toLocaleString()}-${pagination.end.toLocaleString()}`
  const resultRange = pagination.totalCount === undefined
    ? `${range} ${noun}` : `${range} of ${pagination.totalCount.toLocaleString()} ${noun}`
  return <div className="flex flex-wrap items-center justify-between gap-2 p-3">
    <p className="text-xs text-muted-foreground">{resultRange}</p>
    <div className="flex gap-2">
      <Button variant="outline" size="sm" disabled={!pagination.hasPreviousPage || grid.query.isFetching}
        onClick={pagination.previousPage}>Previous</Button>
      <Button variant="outline" size="sm" disabled={!pagination.hasNextPage || grid.query.isFetching}
        onClick={pagination.nextPage}>Next</Button>
    </div>
  </div>
}

function DataGridColumnChooser() {
  const { grid } = useGridContext()
  return (
    <FilterMultiSelect
      label="Columns"
      hideLabel
      values={grid.selectedColumns}
      allLabel="Columns"
      items={grid.schema.columns.map((column) => ({
        value: column.id,
        label: column.label,
      }))}
      onChange={grid.setSelectedColumns}
    />
  )
}

function DataGridTable() {
  const {
    grid,
    caption,
    regionLabel,
    noun = 'items',
    singularNoun,
    renderers,
    emptyMessage,
    onRowOpen,
    rowActionLabel,
    selectedRowKeys,
    rowDetail,
    additionalColumns = [],
    columnChooserColumns,
  } = useGridContext()
  const detailIdPrefix = useId()
  const columns = grid.orderedColumns.map((id) => grid.schema.columns
    .find((column) => column.id === id)!).filter((column) =>
    column && (!columnChooserColumns || columnChooserColumns.includes(column.id)))
  const visibleColumnIds = [
    ...columns.map((column) => column.id),
    ...additionalColumns.map((column) => column.id),
  ]
  const announce = useAnnounce()
  const moveHintId = useId()
  const reorderable = columns.filter((column) => column.id in grid.contract.columns)
  const moveColumn = (from: number, to: number) => {
    if (to < 0 || to >= reorderable.length || to === from) return false
    const order = reorderable.map((column) => column.id)
    order.splice(to, 0, ...order.splice(from, 1))
    grid.setColumnOrder(order)
    announce(`${reorderable[from]!.label} moved to position ${to + 1} of ${visibleColumnIds.length}`)
    return true
  }
  const sortable = useSortable(reorderable.length, moveColumn)
  const previewColumns = [
    ...sortable.order.map((index) => reorderable[index]!),
    ...columns.filter((column) => !(column.id in grid.contract.columns)),
  ]
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const resizeSession = useRef<{ column: string; widths: Record<string, number> } | null>(null)
  const [storedSizing, setColumnSizing] = useState<ColumnSizing>(AUTO_COLUMN_SIZING)
  const [containerWidth, setContainerWidth] = useState(0)
  const columnSignature = JSON.stringify(visibleColumnIds)
  const layoutKey = JSON.stringify([grid.sourceKey, columnSignature, containerWidth])
  const columnSizing = storedSizing.layoutKey === layoutKey ? storedSizing : {
    ...storedSizing,
    widths: Object.fromEntries(Object.entries(storedSizing.widths)
      .filter(([column]) => storedSizing.manual.has(column))),
  }
  const hasItems = grid.items.length > 0

  useLayoutEffect(() => {
    if (!scrollElement) return
    const updateWidth = () => setContainerWidth(scrollElement.clientWidth)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(scrollElement)
    return () => observer.disconnect()
  }, [scrollElement])

  useLayoutEffect(() => {
    if (!hasItems || containerWidth <= 0 || storedSizing.layoutKey === layoutKey) return
    const table = tableRef.current
    if (!table) return
    function captureWidths() {
      if (!table?.querySelector('tbody tr[data-index]')) return
      const widths = measureColumnWidths(table)
      if (Object.values(widths).some((width) => width <= 0) || Object.keys(widths).length === 0) return
      setColumnSizing((current) => current.layoutKey === layoutKey
        ? current
        : {
          ...current,
          layoutKey,
          widths: {
            ...Object.fromEntries(Object.entries(current.widths)
              .filter(([column]) => current.manual.has(column))),
            ...widths,
          },
        })
    }
    const observer = new ResizeObserver(captureWidths)
    observer.observe(table)
    const frame = requestAnimationFrame(captureWidths)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [hasItems, containerWidth, layoutKey, storedSizing.layoutKey])
  const setResizeSession = (column: string, dragging: boolean) => {
    resizeSession.current = dragging
      ? { column, widths: measureColumnWidths(tableRef.current) }
      : null
  }
  const resizeColumn = (column: string, width: number) => {
    const measured = measureColumnWidths(tableRef.current)
    const startingWidths = resizeSession.current?.column === column
      ? resizeSession.current.widths
      : measured
    setColumnSizing((current) => {
      return {
        layoutKey,
        widths: resizeColumnAndTrailing(
          startingWidths,
          visibleColumnIds,
          column,
          width,
        ),
        manual: new Set(current.manual).add(column),
      }
    })
  }
  const autoSizeColumn = (column: string) => setColumnSizing((current) => {
    if (!current.manual.has(column)) return current
    const manual = new Set(current.manual)
    manual.delete(column)
    return {
      ...current,
      layoutKey: '',
      widths: Object.fromEntries(Object.entries(current.widths)
        .filter(([id]) => manual.has(id))),
      manual,
    }
  })
  const pager = useLoadMore({
    ...grid.query,
    data: {
      pages: [{ items: grid.items, pageInfo: { hasNextPage: grid.canLoadMore } }],
    },
    hasNextPage: grid.canLoadMore,
  }, noun, `Load more ${noun}`, {
    criteriaKey: JSON.stringify([grid.sourceKey, grid.activeCriteria]),
    singularNoun,
  })
  const entries = pager.items.flatMap((row) =>
    rowDetail?.hasDetail(row) && rowDetail.isExpanded(row)
      ? [{ row, detail: false }, { row, detail: true }]
      : [{ row, detail: false }])
  const columnCount = previewColumns.length + additionalColumns.length
  const headers = columns.map((_, position) => {
    const column = previewColumns[position]!
    const index = sortable.order[position]
    const reorder = index === undefined ? undefined : {
      hintId: moveHintId,
      position: index,
      count: reorderable.length,
      dragged: sortable.isDragged(index),
      onMoveKey: (step: -1 | 1) => moveColumn(index, index + step),
      ...sortable.itemProps(index, position, false),
    }
    const sort = grid.activeCriteria.sort?.find((entry) => entry.column === column.id)
    const filterActive = grid.activeCriteria.filter?.some((filter) =>
      filter.column === column.id) ?? false
    const custom = renderers?.[column.id]?.header
    return (
      <DataGridColumnHeader
        key={column.id}
        columnId={column.id}
        last={column.id === visibleColumnIds.at(-1)}
        label={column.label}
        width={columnSizing.widths[column.id]}
        auto={!columnSizing.manual.has(column.id)}
        onWidthChange={(width) => resizeColumn(column.id, width)}
        onDragChange={(dragging) => setResizeSession(column.id, dragging)}
        onAutoSize={() => autoSizeColumn(column.id)}
        reorder={reorder}
        sortable={column.sortable}
        direction={sort?.direction}
        onSort={() => grid.toggleSort(column.id)}
        filterActive={filterActive}
        filterContent={column.filterable ? (open) => (
          <DataGridFilterControl column={column} presentation="content" open={open} />
        ) : undefined}
        onFilterOpenChange={(open) => {
          if (open && column.kind === 'category' && column.operators.includes('in')) {
            void grid.loadFacetOptions(column.id)
          }
        }}
      >
        {custom?.({
          column,
          sort,
          toggleSort: () => grid.toggleSort(column.id),
          grid,
        })}
      </DataGridColumnHeader>
    )
  })
  headers.push(...additionalColumns.map((column) => (
    <DataGridColumnHeader
      key={column.id}
      columnId={column.id}
      last={column.id === visibleColumnIds.at(-1)}
      label={column.label}
      width={columnSizing.widths[column.id]}
      auto={!columnSizing.manual.has(column.id)}
      onWidthChange={(width) => resizeColumn(column.id, width)}
      onDragChange={(dragging) => setResizeSession(column.id, dragging)}
      onAutoSize={() => autoSizeColumn(column.id)}
    />
  )))

  const clearedResults = grid.query.data === null && !grid.query.isFetching && !grid.query.isError
  if (!grid.hasDisplayedData && clearedResults) return (
    <RetryNotice
      className="p-6"
      message="Results are unavailable. Try again."
      onRetry={() => void grid.query.refetch()}
    />
  )
  if (!grid.hasDisplayedData && (grid.query.isLoading || grid.query.isFetching || grid.awaitingDefaultView || grid.searchDebouncing)) {
    return <LoadingNotice message={`Loading ${noun}…`} />
  }
  if (!grid.hasDisplayedData && (grid.query.isLoadingError
    || (grid.query.isRefetchError && !grid.query.isFetchNextPageError))) return (
    <RetryNotice
      className="p-6"
      message={grid.query.error.message}
      onRetry={() => void grid.query.refetch()}
    />
  )
  return (
    <div
      ref={setScrollElement}
      data-slot="data-grid-table"
      role="region"
      aria-label={regionLabel ?? caption}
      aria-busy={grid.isUpdating}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to focus and scroll the virtualized region.
      tabIndex={0}
      {...sortable.listProps}
      className="min-h-64 flex-1 scroll-pt-8 overflow-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring/30 [&_[data-slot=data-table]]:overflow-visible [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-background"
    >
      <p id={moveHintId} hidden>Drag, press for move options, or use the left and right arrow keys to move the column.</p>
      {grid.isUpdating ? <DataGridUpdatingCue key={grid.sourceKey} noun={noun} /> : null}
      {grid.hasDisplayedData && grid.query.isError && !grid.query.isFetchNextPageError ? (
        <div className="flex items-center gap-2 px-3 py-2" role="alert">
          <span className="text-destructive text-xs">
            {grid.query.error?.message ?? `Couldn’t update ${noun}.`}
          </span>
          <Button type="button" size="sm" variant="outline" onClick={() => void grid.query.refetch()}>
            Retry
          </Button>
        </div>
      ) : null}
      <DataTable
        tableRef={tableRef}
        tableStyle={sizedTableStyle(columnSizing.widths, visibleColumnIds)}
        fixedLayout={visibleColumnIds.length > 0
          && visibleColumnIds.every((column) => columnSizing.widths[column] !== undefined)}
        caption={caption}
        headers={headers}
        empty={pager.items.length === 0}
        emptyMessage={emptyMessage ?? `No ${noun} match.`}
        resultStatus={{
          count: grid.isPreviousData ? null : pager.items.length,
          hasMore: pager.hasNextPage,
          singular: singularNoun ?? noun.replace(/s$/, ''),
          plural: noun,
          changeKey: JSON.stringify([grid.sourceKey, grid.activeCriteria]),
        }}
        rowCount={pager.hasNextPage ? -1 : entries.length + 1}
      >
        <VirtualTableRows
          items={entries}
          getKey={(entry) => entry.detail ? `${entry.row.key}:detail` : entry.row.key}
          estimateSize={46}
          scrollElement={scrollElement}
          columnCount={columnCount}
          renderRow={({ row, detail }, rowProps) => {
            const detailId = `${detailIdPrefix}-detail-${encodeURIComponent(row.key)}`
            if (detail) return (
              <tr {...rowProps} id={detailId} data-slot="data-grid-row-detail" className="border-border border-b">
                <td colSpan={columnCount} className="px-3 pb-3">{rowDetail?.render(row)}</td>
              </tr>
            )
            const typedRow = Object.assign(row, {
              getValue: (column: string) => row.values[column],
            })
            const expandable = rowDetail?.hasDetail(row) ?? false
            const expanded = expandable && (rowDetail?.isExpanded(row) ?? false)
            return (
              <tr
                {...rowProps}
                tabIndex={onRowOpen ? 0 : undefined}
                aria-label={onRowOpen
                  ? rowActionLabel?.(row) ?? `View ${String(row.values.name ?? row.key)}`
                  : undefined}
                aria-selected={selectedRowKeys ? selectedRowKeys.includes(row.key) : undefined}
                onClick={() => {
                  if (onRowOpen) onRowOpen(row)
                  else if (expandable) rowDetail?.onToggle(row)
                }}
                onKeyDown={(event) => {
                  if (onRowOpen && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    onRowOpen(row)
                  }
                }}
                className={cn(
                  'border-border hover:bg-muted border-b outline-none',
                  onRowOpen && 'focus-visible:bg-muted cursor-pointer',
                  selectedRowKeys?.includes(row.key) && 'bg-primary/10 hover:bg-primary/15',
                  expanded && 'bg-muted/30 border-b-0',
                )}
              >
                {previewColumns.map((column, position) => {
                  const renderer = renderers?.[column.id]?.cell
                  const value = row.values[column.id]
                  const index = sortable.order[position]
                  const content = renderer
                    ? renderer({ row: typedRow, column, value, grid })
                    : formatValue(value, column.labels)
                  return (
                    <td
                      key={column.id}
                      className={cn(
                        'text-muted-foreground overflow-hidden px-3 py-2',
                        index !== undefined && sortable.isDragged(index) && DRAGGED_CELL,
                      )}
                      style={fixedColumnStyle(columnSizing.widths[column.id])}
                    >
                      {rowDetail && position === 0 ? (
                        <div className="flex items-center gap-1.5">
                          {expandable ? (
                            <button
                              type="button"
                              aria-expanded={expanded}
                              aria-controls={expanded ? detailId : undefined}
                              aria-label={rowDetail.label(row)}
                              onClick={(event) => {
                                event.stopPropagation()
                                rowDetail.onToggle(row)
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
                              }}
                              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 -ml-1 shrink-0 rounded-sm p-0.5 outline-none focus-visible:ring-2"
                            >
                              <ChevronRight aria-hidden className={cn('size-3.5 transition-transform', expanded && 'rotate-90')} />
                            </button>
                          ) : <span aria-hidden className="w-4.5 shrink-0" />}
                          <div className="min-w-0 flex-1">{content}</div>
                        </div>
                      ) : content}
                    </td>
                  )
                })}
                {additionalColumns.map((column) => (
                  <td
                    key={column.id}
                    className="overflow-hidden px-3 py-2"
                    style={fixedColumnStyle(columnSizing.widths[column.id])}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            )
          }}
        />
      </DataTable>
      {!grid.pagination && <LoadMoreSentinel
        pager={pager}
        root={scrollElement}
        paused={grid.searchDebouncing || grid.isPreviousData || grid.isUpdating}
        className="m-3"
      />}
    </div>
  )
}

function DataGridUpdatingCue({ noun }: { noun: string }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), 300)
    return () => clearTimeout(timeout)
  }, [])
  if (!visible) return null
  return (
    <div aria-hidden="true" className="pointer-events-none sticky top-8 left-0 z-20 h-0 w-full">
      <span className="bg-background text-muted-foreground absolute top-2 right-3 rounded-md border px-2 py-1 text-xs shadow-sm">
        Updating {noun}…
      </span>
    </div>
  )
}
function DataGridColumnHeader({
  columnId,
  last,
  label,
  width,
  auto,
  onWidthChange,
  onDragChange,
  onAutoSize,
  reorder,
  sortable = false,
  direction,
  onSort,
  filterContent,
  filterActive,
  onFilterOpenChange,
  children,
}: {
  columnId: string
  last: boolean
  label: string
  width?: number
  auto: boolean
  onWidthChange: (width: number) => void
  onDragChange: (dragging: boolean) => void
  onAutoSize: () => void
  reorder?: ColumnReorder
  sortable?: boolean
  direction?: 'asc' | 'desc'
  onSort?: () => void
  filterContent?: (open: boolean) => ReactNode
  filterActive?: boolean
  onFilterOpenChange?: (open: boolean) => void
  children?: ReactNode
}) {
  const headerRef = useRef<HTMLTableCellElement>(null)
  const handleRef = useRef<HTMLButtonElement>(null)
  const refocusAt = useRef<number | null>(null)
  const dragStarted = useRef(false)
  const pressedWhileOpen = useRef(false)
  const closingPress = useRef<Event | null>(null)
  const [moveMenuOpen, setMoveMenuOpen] = useState(false)
  const position = reorder?.position
  useEffect(() => {
    if (!moveMenuOpen) return
    const close = () => setMoveMenuOpen(false)
    document.addEventListener('dragstart', close)
    return () => document.removeEventListener('dragstart', close)
  }, [moveMenuOpen])
  useLayoutEffect(() => {
    if (refocusAt.current === null) return
    if (refocusAt.current === position) handleRef.current?.focus()
    refocusAt.current = null
  }, [position])
  const resizer = (
    <ResizeHandle
      label={`Resize ${label} column`}
      orientation="vertical"
      value={Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width ?? MIN_COLUMN_WIDTH))}
      min={MIN_COLUMN_WIDTH}
      max={MAX_COLUMN_WIDTH}
      measure={() => headerRef.current?.getBoundingClientRect().width}
      onChange={onWidthChange}
      onDragChange={onDragChange}
      onReset={onAutoSize}
      auto={auto}
      className={last ? 'right-0' : '-right-1'}
    />
  )
  const style = fixedColumnStyle(width)
  const armDrag = () => {
    const header = headerRef.current
    if (!header) return
    dragStarted.current = false
    header.draggable = true
    const disarm = () => {
      header.draggable = false
      document.removeEventListener('pointerup', disarm)
      document.removeEventListener('pointercancel', disarm)
    }
    document.addEventListener('pointerup', disarm)
    document.addEventListener('pointercancel', disarm)
  }
  const move = (step: -1 | 1) => {
    if (reorder?.onMoveKey(step)) refocusAt.current = reorder.position + step
  }
  const handle = reorder ? (
    <>
      <button
        ref={handleRef}
        type="button"
        data-slot="column-drag-handle"
        aria-label={`Move ${label} column`}
        aria-describedby={reorder.hintId}
        aria-haspopup="menu"
        aria-expanded={moveMenuOpen}
        onPointerDown={(event) => {
          pressedWhileOpen.current = moveMenuOpen || event.nativeEvent === closingPress.current
          armDrag()
        }}
        // Opened here rather than by a menu trigger, which opens on mousedown and would show on every drag.
        onClick={(event) => {
          const keyboard = event.detail === 0
          if (keyboard || (!dragStarted.current && !pressedWhileOpen.current)) setMoveMenuOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
          event.preventDefault()
          if (refocusAt.current !== null) return
          move(event.key === 'ArrowLeft' ? -1 : 1)
        }}
        className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/30 absolute top-1/2 left-0 flex size-6 -translate-y-1/2 cursor-grab items-center justify-center rounded-sm opacity-0 outline-hidden transition-opacity group-focus-within/header:opacity-100 group-hover/header:opacity-100 focus-visible:ring-2 active:cursor-grabbing motion-reduce:transition-none"
      >
        <GripVertical aria-hidden className="size-3" />
      </button>
      <DropdownMenu
        open={moveMenuOpen}
        onOpenChange={(open, details) => {
          if (!open && details.reason === 'outside-press') closingPress.current = details.event ?? null
          setMoveMenuOpen(open)
        }}
        modal={false}
      >
        <DropdownMenuContent anchor={handleRef} align="start" finalFocus={handleRef} className="min-w-32">
          <DropdownMenuItem disabled={reorder.position === 0} onClick={() => move(-1)}>Move left</DropdownMenuItem>
          <DropdownMenuItem disabled={reorder.position === reorder.count - 1} onClick={() => move(1)}>Move right</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  ) : undefined
  const draggedClass = reorder?.dragged ? cn(DRAGGED_ITEM, 'border') : undefined
  const dragProps = reorder ? {
    handle,
    onDragStart: (event: DragEvent<HTMLTableCellElement>) => {
      if (!event.currentTarget.draggable) return
      dragStarted.current = true
      reorder.onDragStart(event)
    },
    onDragOver: reorder.onDragOver,
    onDragEnd: (event: DragEvent<HTMLTableCellElement>) => {
      event.currentTarget.draggable = false
      reorder.onDragEnd()
    },
  } : {}

  if (children === undefined && (sortable || filterContent)) {
    return (
      <SortableTh
        ref={headerRef}
        data-column-id={columnId}
        {...dragProps}
        className={draggedClass}
        label={label}
        direction={direction}
        onSort={sortable ? onSort : undefined}
        filterContent={filterContent}
        filterActive={filterActive}
        onFilterOpenChange={onFilterOpenChange}
        style={style}
        resizer={resizer}
      />
    )
  }
  return (
    <Th
      ref={headerRef}
      data-column-id={columnId}
      aria-label={label}
      {...dragProps}
      className={cn('group/header relative', draggedClass)}
      style={style}
    >
      {children ?? label}{resizer}
    </Th>
  )
}

function fixedColumnStyle(width: number | undefined) {
  return width === undefined ? undefined : { width, minWidth: width, maxWidth: width }
}

function sizedTableStyle(widths: Record<string, number>, columns: readonly string[]) {
  const values = columns.flatMap((column) => widths[column] ?? [])
  return values.length !== columns.length || values.length === 0
    ? undefined
    : { width: values.reduce((sum, width) => sum + width, 0) }
}

function measureColumnWidths(table: HTMLTableElement | null) {
  if (!table) return {}
  return Object.fromEntries(
    [...table.querySelectorAll<HTMLTableCellElement>('thead th[data-column-id]')]
      .map((header) => [
        header.dataset.columnId!,
        header.getBoundingClientRect().width,
      ]),
  )
}

function resizeColumnAndTrailing(
  widths: Record<string, number>,
  columns: readonly string[],
  column: string,
  width: number,
) {
  const currentWidth = widths[column]
  if (currentWidth === undefined) return { ...widths, [column]: width }
  const trailing = columns.slice(columns.indexOf(column) + 1)
    .filter((id) => widths[id] !== undefined)
  if (trailing.length === 0) return { ...widths, [column]: width }

  const currentTrailingWidth = trailing.reduce((sum, id) => sum + widths[id]!, 0)
  const nextTrailingWidth = Math.max(
    MIN_COLUMN_WIDTH * trailing.length,
    currentTrailingWidth - (width - currentWidth),
  )
  return {
    ...widths,
    [column]: width,
    ...distributeColumnWidths(widths, trailing, nextTrailingWidth),
  }
}

function distributeColumnWidths(
  widths: Record<string, number>,
  columns: readonly string[],
  total: number,
) {
  const distributed: Record<string, number> = {}
  let remaining = [...columns]
  let remainingTotal = total

  while (remaining.length > 0) {
    const weight = remaining.reduce((sum, column) => sum + widths[column]!, 0)
    const belowMinimum = remaining.filter((column) =>
      (remainingTotal * widths[column]! / weight) < MIN_COLUMN_WIDTH)
    if (belowMinimum.length === 0) {
      for (const column of remaining) {
        distributed[column] = remainingTotal * widths[column]! / weight
      }
      break
    }
    for (const column of belowMinimum) distributed[column] = MIN_COLUMN_WIDTH
    remainingTotal -= MIN_COLUMN_WIDTH * belowMinimum.length
    remaining = remaining.filter((column) => !belowMinimum.includes(column))
  }

  return distributed
}

function formatValue(value: unknown, labels?: Readonly<Record<string, string>> | null) {
  if (value === null || value === undefined || value === '') return 'Not available'
  if (typeof value === 'string') return labels?.[value] ?? value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return 'Available'
}

export const DataGrid = Object.assign(DataGridRoot, {
  Root: DataGridRoot,
  Search: DataGridSearch,
  Filter: DataGridFilter,
  Filters: DataGridFilters,
  ColumnChooser: DataGridColumnChooser,
  Table: DataGridTable,
  LoadMore: DataGridLoadMore,
  SavedViews: DataGridSavedViews,
})
